/**
 * Builds Gemini multi-turn `contents` (and the legacy flat transcript) from saved chat
 * messages. Assistant messages carry their tool trace in `chat_messages.tool_call`
 * (`{v: 2, steps, cards}`), so a later turn sees the data an earlier turn fetched as real
 * functionCall/functionResponse pairs instead of re-fetching or guessing.
 *
 * Pure module (no React, no network).
 */

import { toGeminiName } from "./toolSchema";

/** Max saved messages replayed to the model (sliding window). */
export const MAX_HISTORY_MESSAGES = 24;

/**
 * Gemini 3+ validates thought signatures on function calls. Calls replayed from history
 * were not produced in this request, so they carry the documented bypass value.
 */
export const REPLAYED_CALL_SIGNATURE = "skip_thought_signature_validator";

/**
 * Read the v2 tool record saved on an assistant message.
 * @param {object} message - Saved chat message.
 * @returns {{steps: Array<object>, cards: Array<object>} | null} Tool record.
 */
export const readToolRecord = (message) => {
  const record = message?.tool_call;
  if (!record || typeof record !== "object" || record.v !== 2) return null;
  return {
    steps: Array.isArray(record.steps) ? record.steps : [],
    cards: Array.isArray(record.cards) ? record.cards : [],
  };
};

/**
 * Model-facing response for a replayed step, reflecting the final state of its card (a
 * confirmation the user accepted after the turn ended, for example).
 * @param {object} step - Saved step.
 * @param {Array<object>} cards - Saved cards of the same message.
 * @returns {object} functionResponse payload.
 */
const replayedResponse = (step, cards) => {
  const card = step.cardId ? cards.find((item) => item.id === step.cardId) : null;
  const response = { ok: step.ok !== false };
  if (step.summary) response.summary = step.summary;
  if (step.result) response.result = step.result;
  if (card) {
    response.cardStatus = card.status;
    if (card.outcome) response.outcome = card.outcome;
  }
  return response;
};

/**
 * Append a content entry, merging consecutive entries of the same role.
 * @param {Array<object>} contents - Contents being built.
 * @param {'user'|'model'} role - Role.
 * @param {Array<object>} parts - Parts to append.
 * @returns {void}
 */
export const pushContent = (contents, role, parts) => {
  const usable = (parts || []).filter(Boolean);
  if (!usable.length) return;
  const last = contents[contents.length - 1];
  if (last && last.role === role) {
    last.parts = [...last.parts, ...usable];
    return;
  }
  contents.push({ role, parts: usable });
};

/**
 * Convert saved messages into Gemini contents (oldest first, last `maxMessages` only).
 * Steps of tools that are not declared this turn (`knownNames`, Gemini names; e.g. steps the
 * server agent ran) are folded into a text note: Gemini rejects a replayed call to an
 * undeclared function.
 * @param {Array<object>} messages - Saved chat messages.
 * @param {{maxMessages?: number, knownNames?: Set<string>|null}} [options] - Options (no `knownNames`: replay every step).
 * @returns {Array<{role: 'user'|'model', parts: Array<object>}>} Contents.
 */
export const buildHistoryContents = (messages, { maxMessages = MAX_HISTORY_MESSAGES, knownNames = null } = {}) => {
  const sliced = (messages || []).filter((m) => m && (m.role === "user" || m.role === "assistant")).slice(-maxMessages);
  // Start the window on a user message that has text: a window that opens on an assistant
  // message would replay its functionResponse without the functionCall before it (Gemini 400).
  const firstUser = sliced.findIndex((m) => m.role === "user" && String(m.content || "").trim());
  const windowed = firstUser === -1 ? [] : sliced.slice(firstUser);
  const contents = [];
  for (const message of windowed) {
    const text = String(message.content || "").trim();
    if (message.role === "user") {
      pushContent(contents, "user", text ? [{ text }] : []);
      continue;
    }
    const record = readToolRecord(message);
    const allSteps = (record?.steps || []).filter((step) => step && step.name);
    const steps = knownNames ? allSteps.filter((step) => knownNames.has(toGeminiName(step.name))) : allSteps;
    const folded = knownNames ? allSteps.filter((step) => !knownNames.has(toGeminiName(step.name))) : [];
    if (steps.length) {
      pushContent(
        contents,
        "model",
        steps.map((step, index) => ({
          functionCall: { name: toGeminiName(step.name), args: step.args || {} },
          ...(index === 0 ? { thoughtSignature: REPLAYED_CALL_SIGNATURE } : {}),
        }))
      );
      pushContent(
        contents,
        "user",
        steps.map((step) => ({
          functionResponse: { name: toGeminiName(step.name), response: replayedResponse(step, record.cards) },
        }))
      );
    }
    const note = folded.length
      ? `(Earlier steps: ${folded.map((step) => `${step.label || step.name}${step.summary ? `: ${step.summary}` : ""}`).join("; ").slice(0, 1500)})`
      : "";
    const body = [note, text].filter(Boolean).join("\n");
    pushContent(contents, "model", body ? [{ text: body }] : []);
  }
  // Defensive: the first entry must be a user turn without orphaned function responses.
  while (contents.length) {
    const head = contents[0];
    if (head.role === "user") {
      const parts = head.parts.filter((part) => !part.functionResponse);
      if (parts.length) {
        head.parts = parts;
        break;
      }
    }
    contents.shift();
  }
  return contents;
};

/**
 * Flat transcript for the legacy text-JSON protocol (old proxy without `contents`).
 * @param {Array<object>} messages - Saved messages plus the current turn's working entries.
 * @param {{maxMessages?: number}} [options] - Options.
 * @returns {string} Transcript lines.
 */
export const buildLegacyTranscript = (messages, { maxMessages = MAX_HISTORY_MESSAGES } = {}) =>
  (messages || [])
    .filter((m) => m && (m.role === "user" || m.role === "assistant"))
    .slice(-maxMessages)
    .map((message) => {
      const record = message.role === "assistant" ? readToolRecord(message) : null;
      const steps = (record?.steps || [])
        .map((step) => `[Inky used ${step.name}${step.summary ? `: ${step.summary}` : ""}]`)
        .join("\n");
      const body = [steps, String(message.content || "").trim()].filter(Boolean).join("\n");
      return `${message.role === "user" ? "USER" : "INKY"}: ${body}`;
    })
    .join("\n");
